import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import { deskTextsPost, deskTextsQuery, type DeskTextKind } from '../../api/settingsDeskTexts'
import { Spinner } from '../../ui/Spinner'
import { useMsg } from './AutonomyPage'

const LANGS: [string, string][] = [['en', 'English'], ['ro', 'Romanian']]

const SOURCE_LABEL: Record<string, string> = {
  company: 'set here',
  provider: 'provider default',
  seed: 'platform seed',
}

export function DeskTextsPanel({ scope, companyName }: { scope: 'company' | 'sp'; companyName: string }) {
  const [lang, setLang] = useState('en')
  const q = useQuery(deskTextsQuery(scope, lang))
  const client = useQueryClient()
  const [msg, say] = useMsg()
  const [previewMsg, sayPreview] = useMsg()
  const [kind, setKind] = useState('ack')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [preview, setPreview] = useState<{ subject: string | null; text: string; html: string } | null>(null)

  const rows = q.data?.kinds ?? []
  const selected = useMemo(() => rows.find((k) => k.kind === kind) ?? rows[0], [rows, kind])

  useEffect(() => {
    if (!selected) return
    setKind(selected.kind)
    setSubject(selected.subject ?? '')
    setBody(selected.body)
    setBodyHtml(selected.body_html ?? '')
    setPreview(null)
  }, [selected?.kind, selected?.body, selected?.subject, selected?.body_html])

  if (q.error instanceof ApiError && q.error.status === 403) {
    return (
      <div className="panel panel-default">
        <div className="panel-body text-muted" style={{ fontSize: 12 }}>Desk texts need a Super Admin.</div>
      </div>
    )
  }
  if (!q.data) return <Spinner />

  const d = q.data
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'desk_texts'] })

  const fields = () => ({
    scope,
    lang,
    kind,
    subject,
    body,
    body_html: bodyHtml,
  })

  const save = () => {
    say('...', 'wait')
    void deskTextsPost('/save', fields())
      .then((r) => {
        if (r.ok) {
          say('Saved', 'ok')
          void reload()
        } else say(r.message || r.error || 'Failed', 'bad')
      })
      .catch((e: unknown) => say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad'))
  }

  const reset = () => {
    say('...', 'wait')
    void deskTextsPost('/reset', { scope, lang, kind })
      .then((r) => {
        if (r.ok) {
          say('Reset', 'ok')
          void reload()
        } else say(r.message || r.error || 'Failed', 'bad')
      })
      .catch((e: unknown) => say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad'))
  }

  const onPreview = () => {
    sayPreview('...', 'wait')
    void deskTextsPost('/preview', fields())
      .then((r) => {
        if (r.text === undefined) {
          sayPreview(r.message || r.error || 'Preview failed', 'bad')
          return
        }
        setPreview({ subject: r.subject ?? null, text: r.text, html: r.html ?? '' })
        sayPreview('Preview ready', 'ok')
      })
      .catch((e: unknown) => sayPreview(e instanceof ApiError && e.message ? e.message : 'Preview failed', 'bad'))
  }

  return (
    <div className="panel panel-default" id="oddt" data-scope={scope}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Desk texts</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            Emails and pages the AI desk sends to people, in their language. Provider default applies to all companies until a company saves its own row.
          </span>
        </div>
        {!d.ready && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Needs migration <code>Version20261117120000_DeskThread</code> — apply it from Change Log → Database migrations.
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
          <span>
            Editing: <strong>{scope === 'sp' ? 'provider default (all companies)' : companyName}</strong>
          </span>
          <label style={{ margin: 0 }}>
            Language{' '}
            <select className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={lang} onChange={(e) => setLang(e.target.value)}>
              {LANGS.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 260px) 1fr', gap: 16, marginTop: 12 }}>
          <div style={{ maxHeight: 420, overflow: 'auto', borderRight: '1px solid #eef0f2', paddingRight: 8 }}>
            {rows.map((row: DeskTextKind) => (
              <button
                key={row.kind}
                type="button"
                className={`btn btn-default btn-sm btn-block odtd-kind${row.kind === kind ? ' active' : ''}`}
                style={{ textAlign: 'left', marginBottom: 4 }}
                onClick={() => setKind(row.kind)}
              >
                <strong>{row.kind}</strong>
                <div className="text-muted" style={{ fontSize: 11 }}>{SOURCE_LABEL[row.source] ?? row.source}</div>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12 }}>
            <div className="form-group">
              <label>Subject <span className="text-muted">(empty = thread subject; on reply_footer = display name From)</span></label>
              <input className="form-control input-sm" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Text body</label>
              <textarea className="form-control" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
            </div>
            <div className="form-group">
              <label>HTML body <span className="text-muted">(optional; empty = nl2br from text)</span></label>
              <textarea className="form-control" rows={4} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} />
            </div>
            <div className="text-muted" style={{ fontSize: 11, marginBottom: 8 }}>
              Variables: {(d.variables ?? []).slice(0, 6).join(', ')}…
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary btn-sm" id="odtd-save" disabled={!d.ready} onClick={save}>Save</button>
              <button type="button" className="btn btn-default btn-sm" id="odtd-preview" disabled={!d.ready} onClick={onPreview}>Preview</button>
              <button type="button" className="btn btn-default btn-sm" id="odtd-reset" disabled={!d.ready} onClick={reset}>Reset to default</button>
              {msg}
              {previewMsg}
            </div>
            {preview && (
              <div id="odtd-preview-out" style={{ marginTop: 10, padding: 10, background: '#f6f7f9', borderRadius: 6, maxHeight: 220, overflow: 'auto' }}>
                {preview.subject ? <div><strong>Subject:</strong> {preview.subject}</div> : null}
                <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontFamily: 'inherit' }}>{preview.text}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
